import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { MaskLines, Reveal, ClipImage } from "@/components/motion";
import { CTAButton } from "@/components/CTA";
import Seo from "@/components/Seo";
import { SERVICES, PRICING, BUSINESS } from "@/data";

const INCLUDES = {
  house: ["Dusting all surfaces", "Vacuum & mop floors", "Kitchen wipe-down", "Bathroom sanitising", "Empty bins", "Make beds"],
  deep: ["Everything in standard", "Baseboards & door frames", "Inside microwave & oven", "Vents & light fixtures", "Grout & tile scrub", "Behind & under furniture"],
  move: ["Full empty-home clean", "Inside all cabinets", "Inside fridge & oven", "Closets & shelving", "Window sills & tracks", "Inspection-ready finish"],
  office: ["Desks & workstations", "Common areas & kitchen", "Washroom sanitising", "Floors & carpets", "Trash & recycling", "After-hours scheduling"],
};

export default function Services() {
  return (
    <main data-testid="services-page">
      <Seo
        title="Cleaning Services & Pricing | House, Deep, Move-out & Office | Magical Cleaning Team"
        description="Explore our house cleaning, deep cleaning, move-in/move-out cleaning and office cleaning services with transparent pricing across Kitchener, Waterloo & Cambridge. 20% off your first clean."
        path="/services"
      />
      <section className="mx-auto max-w-[1400px] px-6 pt-36 pb-16 lg:px-10 lg:pt-48">
        <p className="overline text-[var(--brand)]">Services</p>
        <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.92] tracking-tighter sm:text-7xl lg:text-[6rem]">
          <MaskLines lines={["Every clean,", "covered."]} delay={0.1} />
        </h1>
        <Reveal delay={0.5} className="mt-8 max-w-xl text-lg leading-relaxed text-[var(--ink-soft)]">
          From weekly upkeep to deep resets and move-out deep-cleans — pick what you need and we'll handle the rest,
          right across Kitchener, Waterloo & Cambridge.
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 pb-8 lg:px-10">
        {SERVICES.map((s, i) => {
          const flip = i % 2 === 1;
          return (
            <Reveal key={s.id} className="border-t border-[var(--line)] py-16 lg:py-24">
              <div className="grid items-center gap-10 lg:grid-cols-12">
                <div className={`lg:col-span-6 ${flip ? "lg:order-2 lg:col-start-7" : ""}`}>
                  <ClipImage src={s.image} alt={`${s.title} in the Tri-Cities`} className="aspect-[4/3] w-full" />
                </div>
                <div className={`lg:col-span-5 ${flip ? "lg:order-1 lg:col-start-1" : "lg:col-start-8"}`}>
                  <span className="font-mono-accent text-sm text-[var(--brand)]">{s.num}</span>
                  <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">{s.title}</h2>
                  <p className="mt-2 overline text-[var(--ink-soft)]">{s.tag}</p>
                  <p className="mt-6 leading-relaxed text-[var(--ink-soft)]">{s.desc}</p>
                  <ul className="mt-8 grid grid-cols-2 gap-3 text-sm">
                    {INCLUDES[s.id].map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--brand)]" /> {f}
                      </li>
                    ))}
                  </ul>
                  <CTAButton to="/contact" className="mt-9" testId={`service-cta-${s.id}`}>Get Free Quote <ArrowUpRight className="h-4 w-4" /></CTAButton>
                </div>
              </div>
            </Reveal>
          );
        })}
      </section>

      {/* Pricing */}
      <section data-testid="services-pricing" className="border-t border-[var(--line)] bg-[var(--brand-light)]/40">
        <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
          <Reveal className="max-w-2xl">
            <p className="overline text-[var(--brand)]">Pricing</p>
            <h2 className="mt-5 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">Transparent, affordable rates</h2>
            <p className="mt-6 text-lg text-[var(--ink-soft)]">No hidden fees. New customers save <span className="font-semibold text-[var(--brand)]">20%</span> on their first clean.</p>
          </Reveal>

          <div className="mt-14 grid gap-px border border-[var(--line)] bg-[var(--line)] lg:grid-cols-3">
            {PRICING.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08} className={`relative flex flex-col bg-white p-8 lg:p-10 ${p.best ? "ring-1 ring-inset ring-[var(--brand)]" : ""}`}>
                {p.best && <span className="absolute right-6 top-8 bg-[var(--brand)] px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white">Popular</span>}
                <p className="overline text-[var(--brand)]">{p.name}</p>
                <div className="mt-6 flex items-end gap-2">
                  <span className="font-display text-6xl font-extrabold tracking-tighter">${p.price}</span>
                  <span className="mb-2 text-sm text-[var(--ink-soft)]">{p.unit}</span>
                </div>
                <ul className="mt-8 flex-1 space-y-3 text-sm text-[var(--ink-soft)]">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-3"><span className="h-1.5 w-1.5 shrink-0 bg-[var(--brand)]" /> {f}</li>
                  ))}
                </ul>
                <CTAButton to="/contact" variant={p.best ? "solid" : "ghost"} className="mt-8 w-full" testId={`services-pricing-cta-${p.id}`}>Book Now</CTAButton>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[var(--ink)] text-white">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start gap-8 px-6 py-20 lg:flex-row lg:items-center lg:justify-between lg:px-10 lg:py-24">
          <h2 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">Not sure what you need?</h2>
          <div className="flex flex-wrap gap-4">
            <CTAButton to="/contact" testId="services-quote-btn">Get Free Quote</CTAButton>
            <a href={`tel:${BUSINESS.phoneHref}`} data-testid="services-call-btn" className="sweep-btn inline-flex items-center gap-2 border border-white/25 px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-transform hover:-translate-y-0.5">
              <span className="relative z-10">Call {BUSINESS.phoneDisplay}</span>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
